/**
 * FieldRenderer — generic field-to-primitive dispatcher.
 *
 *
 * Reads a `FieldDefinition` and renders the right primitive based on
 * `field.valueType`. The dispatcher is stateless: it reads the
 * current answer from the engine, reads validation issues from the
 * engine's per-field issue map, and writes back through
 * `engine.setAnswer` / `engine.clearAnswer`.
 *
 * `touched` state is owned by the parent (App) and threaded via the
 * `touched` + `onTouch` props per Phase 7b's contract. The parent's
 * touched-key convention is opaque to this dispatcher: callers wrap
 * the field-id with the repeatable instance id when needed.
 *
 * Composite handling (name, address, phone) is a SectionRenderer
 * concern (Slice 5/7); FieldRenderer dispatches one field at a time.
 */
import { useEffect, useState } from 'react';
import type {
  AnswerValue,
  FieldDefinition,
  ValidationIssue,
} from '@inside-us-aos/domain';
import type { FormEngine } from '@inside-us-aos/form-engine';
import {
  BooleanRadio,
  DateInput,
  DocumentUpload,
  type IUploadedFileMetadata,
  MultiSelectInput,
  NumberInput,
  PhoneInput,
  SelectInput,
  TextInput,
} from '@inside-us-aos/ui';

import { resolveFieldOptions } from './optionsResolver';
import { withUserHelpText } from './userHelpText';

export interface FieldRendererProps {
  field: FieldDefinition;
  engine: FormEngine;
  /**
   * Whether the user has interacted with this field. Threaded into
   * the underlying primitive's `touched` prop so errors only display
   * after first interaction (Phase 7b).
   */
  touched: boolean;
  /** Fires when the field is interacted with (change or blur). */
  onTouch: () => void;
  /**
   * Repeatable group instance id when the field is part of a
   * repeatable group; omitted for singleton fields. Threaded through
   * to the engine so the right answer is read / written.
   */
  groupInstanceId?: string;
  /**
   * Upload bridge for `document_upload` fields. When supplied, the
   * dispatcher's DocumentUpload mount calls this on each file
   * selection and replaces the optimistic `local:<name>` placeholder
   * id with the uploaded file id. When omitted, only the placeholder
   * is written. Sourced from the App-level `IAppProps.uploadDocument`.
   */
  uploadDocument?: (
    fieldId: import('@inside-us-aos/domain').FieldId,
    file: IUploadedFileMetadata,
  ) => Promise<{ uploadedFileId: string }>;
  /**
   * Surfaces a per-field upload error. The dispatcher reports failures
   * here and the caller renders them in the section. Optional —
   * unsupplied implies no error surface (placeholder ids only).
   */
  onUploadError?: (fieldId: string, message: string) => void;
}

/**
 * Subscribes to the form engine and bumps a counter on each answer
 * change so React re-renders when the engine state shifts. Mirrors
 * the App-level `useEngineRevision` so a FieldRenderer used outside
 * the App's subscription tree (e.g., in isolated tests) still
 * reflects engine writes immediately.
 */
function useEngineRevision(engine: FormEngine): number {
  const [revision, setRevision] = useState(0);
  useEffect(
    () => engine.subscribe(() => setRevision(prev => prev + 1)),
    [engine],
  );
  return revision;
}

/**
 * Helpers reading / writing engine answers by valueType.
 */
function readText(
  engine: FormEngine,
  field: FieldDefinition,
  groupInstanceId?: string,
): string {
  const v = engine.getAnswer(field.id, groupInstanceId);
  return v && v.type === 'text' ? v.value : '';
}
function readDate(
  engine: FormEngine,
  field: FieldDefinition,
  groupInstanceId?: string,
): string {
  const v = engine.getAnswer(field.id, groupInstanceId);
  return v && v.type === 'date' ? v.value : '';
}
function readSelect(
  engine: FormEngine,
  field: FieldDefinition,
  groupInstanceId?: string,
): string {
  const v = engine.getAnswer(field.id, groupInstanceId);
  return v && v.type === 'select' ? v.value : '';
}
function readMultiSelect(
  engine: FormEngine,
  field: FieldDefinition,
  groupInstanceId?: string,
): readonly string[] {
  const v = engine.getAnswer(field.id, groupInstanceId);
  return v && v.type === 'multi_select' ? v.value : [];
}
function readNumber(
  engine: FormEngine,
  field: FieldDefinition,
  groupInstanceId?: string,
): number | undefined {
  const v = engine.getAnswer(field.id, groupInstanceId);
  return v && v.type === 'number' ? v.value : undefined;
}
function readBoolean(
  engine: FormEngine,
  field: FieldDefinition,
  groupInstanceId?: string,
): boolean | undefined {
  const v = engine.getAnswer(field.id, groupInstanceId);
  return v && v.type === 'boolean' ? v.value : undefined;
}

function getIssues(
  engine: FormEngine,
  field: FieldDefinition,
): readonly ValidationIssue[] {
  return (
    engine.getValidationState().issuesByFieldId.get(field.id) ?? []
  );
}

/**
 * Document-upload section uses the DocumentUpload primitive. The
 * primitive's value lives in component state (Phase 2) — the
 * uploaded-file metadata is staging shape; the engine's
 * `document_upload` AnswerValue stores the resolved
 * `uploadedFileIds`. We mirror the demo's local-id placeholder
 * pattern so a section that only renders fields can still upload
 * documents through the parent's `uploadDocument` prop, when
 * provided.
 */
function FieldRendererDocumentUpload({
  field,
  engine,
  touched,
  onTouch,
  groupInstanceId,
  uploadDocument,
  onUploadError,
}: FieldRendererProps): JSX.Element {
  const [files, setFiles] = useState<IUploadedFileMetadata[]>([]);
  return (
    <DocumentUpload
      field={withUserHelpText(field)}
      value={files}
      issues={getIssues(engine, field)}
      touched={touched}
      onBlur={() => onTouch()}
      onChange={next => {
        setFiles(next);
        onTouch();
        const placeholders = next.map(f => `local:${f.name}`);
        const value: AnswerValue = {
          type: 'document_upload',
          value: { uploadedFileIds: placeholders },
        };
        engine.setAnswer(field.id, value, groupInstanceId);

        if (uploadDocument && next.length > 0) {
          void Promise.all(
            next.map(file =>
              uploadDocument(field.id, file).then(
                ({ uploadedFileId }) => uploadedFileId,
              ),
            ),
          )
            .then(ids => {
              const settled: AnswerValue = {
                type: 'document_upload',
                value: { uploadedFileIds: ids },
              };
              engine.setAnswer(field.id, settled, groupInstanceId);
            })
            .catch((err: unknown) => {
              const message =
                err instanceof Error ? err.message : 'Could not upload file.';
              onUploadError?.(field.id, message);
            });
        }
      }}
    />
  );
}

/**
 * The dispatcher itself. Composite types (`name`, `address`) and
 * runs of related primitive fields are collapsed by the
 * SectionRenderer; FieldRenderer is the per-field leaf. If a caller
 * passes a composite field through here, the dispatcher renders the
 * underlying TextInput layer (the catalog uses primitive valueType
 * for those — `address` and `name` valueTypes don't appear in the
 * AOS v1 catalog today; they're reserved for future use).
 */
export function FieldRenderer(props: FieldRendererProps): JSX.Element | null {
  const { field: rawField, engine, touched, onTouch, groupInstanceId } = props;
  // Re-read on every engine change so the controlled inputs reflect
  // the latest answer immediately. Bumping `revision` triggers a
  // re-render; the read helpers below pick up the new value.
  useEngineRevision(engine);
  // Resolve `required` to a literal boolean before passing the field
  // down to the primitives. The primitives only render the `*` marker
  // for `field.required === true`, so a RuleExpression (conditional
  // required) silently never renders the asterisk on the label.
  // Resolving here gives every input the correct affordance.
  const field: FieldDefinition = {
    ...rawField,
    required:
      engine.getFieldStatus(rawField.id, groupInstanceId).required ||
      rawField.required === true,
  };
  const issues = getIssues(engine, field);

  switch (field.valueType) {
    case 'text': {
      // Phone fields (any id ending in `_phone`) dispatch to PhoneInput
      // so the visible value is masked (XXX) XXX-XXXX while the engine
      // stores digits only. The PDF projector writes the digits-only
      // canonical value into USCIS phone widgets unchanged.
      const isPhoneField = (field.id as string).endsWith('_phone');
      if (isPhoneField) {
        return (
          <PhoneInput
            field={withUserHelpText(field)}
            value={readText(engine, field, groupInstanceId)}
            issues={issues}
            touched={touched}
            onBlur={onTouch}
            onChange={next => {
              engine.setAnswer(
                field.id,
                { type: 'text', value: next },
                groupInstanceId,
              );
              onTouch();
            }}
          />
        );
      }
      return (
        <TextInput
          field={withUserHelpText(field)}
          value={readText(engine, field, groupInstanceId)}
          issues={issues}
          touched={touched}
          onBlur={onTouch}
          onChange={next => {
            engine.setAnswer(
              field.id,
              { type: 'text', value: next },
              groupInstanceId,
            );
            onTouch();
          }}
        />
      );
    }
    case 'date': {
      return (
        <DateInput
          field={withUserHelpText(field)}
          value={readDate(engine, field, groupInstanceId)}
          issues={issues}
          touched={touched}
          onBlur={onTouch}
          onChange={next => {
            engine.setAnswer(
              field.id,
              { type: 'date', value: next },
              groupInstanceId,
            );
            onTouch();
          }}
        />
      );
    }
    case 'number': {
      return (
        <NumberInput
          field={withUserHelpText(field)}
          value={readNumber(engine, field, groupInstanceId)}
          issues={issues}
          touched={touched}
          onBlur={onTouch}
          onChange={next => {
            onTouch();
            if (next === undefined) {
              engine.clearAnswer(field.id, groupInstanceId);
              return;
            }
            engine.setAnswer(
              field.id,
              { type: 'number', value: next },
              groupInstanceId,
            );
          }}
        />
      );
    }
    case 'boolean': {
      return (
        <BooleanRadio
          field={withUserHelpText(field)}
          value={readBoolean(engine, field, groupInstanceId)}
          issues={issues}
          touched={touched}
          onBlur={onTouch}
          onChange={next => {
            engine.setAnswer(
              field.id,
              { type: 'boolean', value: next },
              groupInstanceId,
            );
            onTouch();
          }}
        />
      );
    }
    case 'select': {
      return (
        <SelectInput
          field={withUserHelpText(field)}
          options={resolveFieldOptions(field)}
          value={readSelect(engine, field, groupInstanceId)}
          issues={issues}
          touched={touched}
          onBlur={onTouch}
          onChange={next => {
            engine.setAnswer(
              field.id,
              { type: 'select', value: next },
              groupInstanceId,
            );
            onTouch();
          }}
        />
      );
    }
    case 'multi_select': {
      return (
        <MultiSelectInput
          field={withUserHelpText(field)}
          options={resolveFieldOptions(field)}
          value={readMultiSelect(engine, field, groupInstanceId)}
          issues={issues}
          touched={touched}
          onBlur={onTouch}
          onChange={next => {
            engine.setAnswer(
              field.id,
              { type: 'multi_select', value: next },
              groupInstanceId,
            );
            onTouch();
          }}
        />
      );
    }
    case 'document_upload': {
      return <FieldRendererDocumentUpload {...props} />;
    }
    case 'name':
    case 'address': {
      // Composite valueTypes — SectionRenderer handles these by
      // collapsing related primitive fields into a NameComposite or
      // AddressComposite. FieldRenderer should not be invoked for
      // these directly; render nothing rather than throw so a stray
      // call doesn't crash the page.
      return null;
    }
    default: {
      // Exhaustiveness guard. If a new valueType is added to the
      // catalog, TypeScript flags this branch.
      const _exhaustive: never = field.valueType;
      void _exhaustive;
      return null;
    }
  }
}
